import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { OtherOfficeAcceptanceRequestRepliesGetModel } from "./OtherOfficeAcceptanceRequestReplies";

/**
 * ### 他事業所 受入リクエスト API 操作クラス
 */
export default class OtherOfficeAcceptanceRequests {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OtherOfficeAcceptanceRequests
   */
  public async gets(
    userStore: LoginUser,
    queryParams?: { start: number; end: number; type: "HEAD" | "LOG"; startIndex: number; endIndex: number }
  ): Promise<Array<OtherOfficeAcceptanceRequestsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequests, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<OtherOfficeAcceptanceRequestsGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );
    return response.data;
  }

  /**
   * ### GET OtherOfficeAcceptanceRequestsCount
   */
  public async getCount(userStore: LoginUser, queryParams?: { start: number; end: number; type: "HEAD" | "LOG" }): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequests, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);
    return response.data.count;
  }

  /**
   * ### GET ExistNoreplyOtherOfficeAcceptanceRequest
   */
  public async existNoreply(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.NoreplyOtherOfficeAcceptanceRequestCount, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);
    return response.data.count;
  }

  /**
   * ### POST OtherOfficeAcceptanceRequests
   */
  public async post(userStore: LoginUser, requestBody: OtherOfficeAcceptanceRequestsPostModel): Promise<OtherOfficeAcceptanceRequestsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequests, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH OtherOfficeAcceptanceRequests
   */
  public async patch(userStore: LoginUser, datetime: number, requestBody: OtherOfficeAcceptanceRequestsPatchModel): Promise<OtherOfficeAcceptanceRequestsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequest, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherOfficeAcceptanceRequestsGetModel {
  /** 施設ID */
  facilityId: number;
  /** ソートキー（{type}_{datetime}_{receiverFacilityId}） */
  sortKey: string;
  /** レコード種別文字列（親：HEAD、子：LOG） */
  type: "HEAD" | "LOG";
  /** リクエスト日時 */
  datetime: number;
  /** リクエスト対象の施設ID */
  receiverFacilityId: number;

  /** 利用者（患者）名 */
  residentName: string;
  /** 利用者情報 */
  residentInfo: ResidentInfo | undefined;
  /** メッセージ内容（補足説明等） */
  messageText: string;
  /** 受け入れ可否確認 */
  replyAcceptance: boolean;

  /** 送信した施設 ID（type=HEAD の場合のみ格納する） */
  sendFacilityIds: number[] | undefined;
  /** 返信をしたかどうか（type=LOG の場合のみ格納する） */
  replied: boolean | undefined;
  /** 終了したかどうか */
  ended: boolean;

  /** リクエストしたユーザーID */
  userId: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;

  /** 送信元施設名（typeがLOGの場合のみ格納される） */
  facilityName?: string | undefined;
  /** 返信情報（返信がある場合のみ格納される） */
  reply?: OtherOfficeAcceptanceRequestRepliesGetModel | undefined;
}

export interface ResidentInfo {
  /** 年齢 */
  age: number;
  /** 性別 */
  gender: "男性" | "女性";
  /** 介護度 */
  careLevel: CareLevel;
  /** ADL */
  adl: Adl;
  /** 退所（退院）予定日 */
  outDatetime: number;
}

export enum CareLevel {
  /** 自立 */
  CARE_LEVEL_1000 = "careLevel1000",
  /** 申請中 */
  CARE_LEVEL_1010 = "careLevel1010",
  /** 要支援１ */
  CARE_LEVEL_2010 = "careLevel2010",
  /** 要支援２ */
  CARE_LEVEL_2020 = "careLevel2020",
  /** 要介護１ */
  CARE_LEVEL_3010 = "careLevel3010",
  /** 要介護２ */
  CARE_LEVEL_3020 = "careLevel3020",
  /** 要介護３ */
  CARE_LEVEL_3030 = "careLevel3030",
  /** 要介護４ */
  CARE_LEVEL_3040 = "careLevel3040",
  /** 要介護５ */
  CARE_LEVEL_3050 = "careLevel3050"
}

export interface Adl {
  /** 移動 */
  move: AdlType;
  /** 食事 */
  meel: AdlType;
  /** 入浴 */
  bathing: AdlType;
  /** 排せつ */
  excretion: AdlType;
}

export enum AdlType {
  /** 自立 */
  HEALTHINESS = "healthiness",
  /** 見守り */
  MONITORING = "monitoring",
  /** 一部介助 */
  PARTIAL_ASSISTANCE = "partialAssistance",
  /** 全介助 */
  FULL_ASSISTANCE = "fullAssistance"
}

export interface OtherOfficeAcceptanceRequestsPostModel {
  /** 送信した施設 ID */
  sendFacilityIds: number[];
  /** 利用者（患者）名 */
  residentName: string;
  /** 利用者情報 */
  residentInfo: ResidentInfo | null;
  /** メッセージ内容（補足説明等） */
  messageText: string;
  /** 受け入れ可否確認 */
  replyAcceptance: boolean;
}

export interface OtherOfficeAcceptanceRequestsPatchModel {
  /** 更新種別 */
  updateType: "status" | "message";
  /** メッセージ内容（補足説明等） */
  messageText: string | undefined;
  /** 終了したかどうか */
  ended: boolean | undefined;

  /** 競合検知用の更新日時 */
  updated: number;
}
